import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PreferencesSection = ({ onShowNotification }) => {
  const [preferences, setPreferences] = useState({
    defaultAITool: 'chatgpt',
    defaultUseCase: 'content',
    theme: 'system',
    language: 'en',
    emailNotifications: true,
    promptSuggestions: true,
    autoSave: true,
    keyboardShortcuts: true
  });

  const aiToolOptions = [
    { value: 'chatgpt', label: 'ChatGPT', description: 'OpenAI ChatGPT' },
    { value: 'claude', label: 'Claude', description: 'Anthropic Claude' },
    { value: 'gemini', label: 'Gemini', description: 'Google Gemini' },
    { value: 'midjourney', label: 'Midjourney', description: 'Image generation' },
    { value: 'dalle', label: 'DALL-E', description: 'OpenAI image generation' }
  ];

  const useCaseOptions = [
    { value: 'content', label: 'Content Creation', description: 'Writing and copywriting' },
    { value: 'marketing', label: 'Marketing', description: 'Campaigns and social media' },
    { value: 'coding', label: 'Coding', description: 'Development and debugging' },
    { value: 'design', label: 'Design', description: 'Visual and creative work' },
    { value: 'business', label: 'Business', description: 'Strategy and analysis' }
  ];

  const themeOptions = [
    { value: 'light', label: 'Light', description: 'Light color scheme' },
    { value: 'dark', label: 'Dark', description: 'Dark color scheme' },
    { value: 'system', label: 'System', description: 'Follow system preference' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English', description: 'English (US)' },
    { value: 'es', label: 'Spanish', description: 'Español' },
    { value: 'fr', label: 'French', description: 'Français' },
    { value: 'de', label: 'German', description: 'Deutsch' },
    { value: 'ja', label: 'Japanese', description: '日本語' }
  ];

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const handleSavePreferences = () => {
    onShowNotification('success', 'Preferences Saved', 'Your preferences have been updated successfully.');
  };

  const handleResetPreferences = () => {
    setPreferences({
      defaultAITool: 'chatgpt',
      defaultUseCase: 'content',
      theme: 'system',
      language: 'en',
      emailNotifications: true,
      promptSuggestions: true,
      autoSave: true,
      keyboardShortcuts: true
    });
    onShowNotification('success', 'Preferences Reset', 'All preferences have been reset to defaults.');
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">Preferences</h2>
        <p className="text-sm md:text-base text-muted-foreground mt-1">Customize your experience and default settings</p>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 md:p-8 space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 md:mb-6">Default Settings</h3>
          <div className="space-y-4 md:space-y-6">
            <Select
              label="Default AI Tool"
              description="Your preferred AI tool for new prompts"
              options={aiToolOptions}
              value={preferences?.defaultAITool}
              onChange={(value) => handlePreferenceChange('defaultAITool', value)}
            />

            <Select
              label="Default Use Case"
              description="Your most common prompt category"
              options={useCaseOptions}
              value={preferences?.defaultUseCase}
              onChange={(value) => handlePreferenceChange('defaultUseCase', value)}
            />
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 md:mb-6">Appearance</h3>
          <div className="space-y-4 md:space-y-6">
            <Select
              label="Theme"
              description="Choose your preferred color scheme"
              options={themeOptions}
              value={preferences?.theme}
              onChange={(value) => handlePreferenceChange('theme', value)}
            />

            <Select
              label="Language"
              description="Select your preferred language"
              options={languageOptions}
              value={preferences?.language}
              onChange={(value) => handlePreferenceChange('language', value)}
            />
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 md:mb-6">Features</h3>
          <div className="space-y-4">
            <Checkbox
              label="Email Notifications"
              description="Receive updates about new features and tips"
              checked={preferences?.emailNotifications}
              onChange={(e) => handlePreferenceChange('emailNotifications', e?.target?.checked)}
            />

            <Checkbox
              label="Prompt Suggestions"
              description="Show AI-powered suggestions while typing"
              checked={preferences?.promptSuggestions}
              onChange={(e) => handlePreferenceChange('promptSuggestions', e?.target?.checked)}
            />

            <Checkbox
              label="Auto-Save"
              description="Automatically save prompts as you work"
              checked={preferences?.autoSave}
              onChange={(e) => handlePreferenceChange('autoSave', e?.target?.checked)}
            />

            <Checkbox
              label="Keyboard Shortcuts"
              description="Enable keyboard shortcuts for faster navigation"
              checked={preferences?.keyboardShortcuts}
              onChange={(e) => handlePreferenceChange('keyboardShortcuts', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="default" onClick={handleSavePreferences} iconName="Check" iconPosition="left" fullWidth>
              Save Preferences
            </Button>
            <Button variant="outline" onClick={handleResetPreferences} iconName="RotateCcw" iconPosition="left" fullWidth>
              Reset to Defaults
            </Button>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 md:p-8">
        <h3 className="text-lg font-semibold text-foreground mb-4 md:mb-6">Keyboard Shortcuts</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { keys: ['Ctrl', 'N'], action: 'New Prompt' },
            { keys: ['Ctrl', 'S'], action: 'Save Prompt' },
            { keys: ['Ctrl', 'K'], action: 'Search' },
            { keys: ['Ctrl', 'H'], action: 'View History' },
            { keys: ['Ctrl', ','], action: 'Settings' },
            { keys: ['Esc'], action: 'Close Modal' }
          ]?.map((shortcut, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span className="text-sm text-foreground">{shortcut?.action}</span>
              <div className="flex items-center gap-2">
                {shortcut?.keys?.map((key, i) => (
                  <React.Fragment key={i}>
                    <kbd className="px-2 py-1 text-xs font-semibold bg-card border border-border rounded">
                      {key}
                    </kbd>
                    {i < shortcut?.keys?.length - 1 && <span className="text-muted-foreground">+</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PreferencesSection;